import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ShieldCheck, ExternalLink } from "lucide-react";
import api from "@/lib/api";

export default function PartnersList() {
  const [partners, setPartners] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = "Nos partenaires — Déménageur Pro";
    const meta = (name, content) => {
      let el = document.querySelector(`meta[name="${name}"]`);
      if (!el) { el = document.createElement("meta"); el.setAttribute("name", name); document.head.appendChild(el); }
      el.setAttribute("content", content);
    };
    meta("description", "Liste actualisée des entreprises de déménagement partenaires de Déménageur Pro susceptibles de recevoir votre demande de devis.");
    meta("robots", "index,follow");

    api.get("/companies", { params: { limit: 500 } })
      .then((r) => {
        const items = (r.data?.items || []).filter(
          (c) => c.listing_status === "partner" || c.listing_status === "partner_verified"
        );
        setPartners(items);
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="animate-fade-up" data-testid="page-partenaires">
      <section className="py-12 md:py-16 bg-gradient-to-br from-[#F0FBF6] to-white">
        <div className="container mx-auto px-6 lg:px-10 max-w-4xl">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#2BD391] text-white text-xs font-semibold mb-4">
            <ShieldCheck className="w-3.5 h-3.5" /> Partenaires vérifiés
          </div>
          <h1 className="font-heading font-extrabold text-4xl md:text-5xl text-[#2C3E50] mb-4">
            Nos entreprises partenaires
          </h1>
          <p className="text-lg text-[#5C6B7A] max-w-3xl">
            Voici la liste des entreprises de déménagement partenaires de Déménageur Pro susceptibles de recevoir votre demande de devis
            lorsque vous soumettez un formulaire. Une demande est transmise à un <strong>maximum de 5 entreprises</strong>
            correspondant à votre projet et votre zone géographique.
          </p>
        </div>
      </section>

      <section className="py-12">
        <div className="container mx-auto px-6 lg:px-10 max-w-4xl">
          {loading ? (
            <p className="text-[#5C6B7A]">Chargement…</p>
          ) : partners.length === 0 ? (
            <div className="p-8 border-2 border-dashed border-[#E5E7EB] rounded-2xl text-center text-[#5C6B7A]">
              Aucune entreprise partenaire pour le moment.
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="partners-grid">
              {partners.map((p) => (
                <Link
                  key={p.slug}
                  to={`/demenageur/${p.slug}`}
                  className="p-5 bg-white border-2 border-[#E5E7EB] rounded-2xl hover:border-[#2BD391] hover:shadow-md transition-all"
                  data-testid={`partner-item-${p.slug}`}
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1">
                      <div className="font-heading font-bold text-lg text-[#2C3E50]">{p.name}</div>
                      <div className="text-sm text-[#5C6B7A] mt-1">{p.city} · {p.department}</div>
                      <div className="mt-2 text-xs text-[#5C6B7A]">SIRET : {p.siret || "—"}</div>
                    </div>
                    <ExternalLink className="w-4 h-4 text-[#5C6B7A]" />
                  </div>
                </Link>
              ))}
            </div>
          )}

          <p className="text-xs text-[#8A98A5] italic mt-8">
            Cette liste est mise à jour dynamiquement. Toute demande de devis n'est transmise qu'aux partenaires figurant sur cette page,
            dans la limite de 5 par demande, correspondant à votre zone géographique et à votre projet.
          </p>
        </div>
      </section>
    </div>
  );
}
